import { ReactNode } from 'react';

import cn from 'classnames';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './InfoBlocks.module.scss';

const baseClass = 'info-blocks';

export type InfoItem = {
  title: string;
  icon?: string;
  items?: string[];
  description?: string;
};

type Props = {
  title?: ReactNode;
  description?: string;
  items: InfoItem[];
  theme?: 'dark' | 'light' | 'light-dark';
  layout?: 'row' | 'column' | 'column-4';
  className?: string;
  hasCheckItems?: boolean;
  coloredIcons?: boolean;
  extra?: ReactNode;
};

export const InfoBlocks = ({
  title,
  theme = 'dark',
  layout = 'row',
  description,
  items,
  hasCheckItems = false,
  coloredIcons = false,
  className,
  extra,
}: Props) => {
  const introBlockTheme = theme === 'light-dark' ? 'dark' : theme;

  return (
    <section
      className={cn(
        styles[`${baseClass}-wrapper`],
        styles[`${baseClass}-${theme}`],
        coloredIcons && styles[`${baseClass}-colored-icons`],
        className,
      )}
    >
      <div className={styles[`${baseClass}-container`]}>
        {(title || description) && (
          <TitleBlock title={title} description={description} theme={introBlockTheme} extra={extra} />
        )}

        <div className={cn(styles[`${baseClass}-items`], styles[`${baseClass}-layout-${layout}`])}>
          {items.map((block, idx) => (
            <div key={idx} className={styles[`${baseClass}-item`]}>
              {block.icon && <Icon type={block.icon} className={styles[`${baseClass}-icon`]} />}

              <h3 className={styles[`${baseClass}-title`]}>{block.title}</h3>

              {block.description && <p className={styles[`${baseClass}-description`]}>{block.description}</p>}

              {block.items?.length && (
                <ul
                  className={cn(styles[`${baseClass}-list`], hasCheckItems && styles[`${baseClass}-list--has-check`])}
                >
                  {block.items.filter(Boolean).map((el, i) => (
                    <li key={i} className={styles[`${baseClass}-list-item`]}>
                      {hasCheckItems && <Icon type="check-10" />}

                      {el}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {layout === 'column' && items.length === 6 && <div className={styles[`${baseClass}-divider`]} />}
        </div>
      </div>
    </section>
  );
};
